import { useState, useEffect } from 'react';
import { Card, Form, Button, Alert, Badge, Row, Col } from 'react-bootstrap';
import { Icon } from '@iconify/react';
import api from '../lib/api';
import { useAuth } from '../lib/auth.jsx';
import PageHeader from '../components/PageHeader.jsx';

export default function ProfilePage() {
  const { user, refreshMe } = useAuth();

  // Stammdaten (Email + Name)
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [profileBusy, setProfileBusy] = useState(false);
  const [profileMsg, setProfileMsg] = useState(null);
  useEffect(() => {
    if (user) {
      setEmail(user.email || '');
      setDisplayName(user.display_name || '');
    }
  }, [user]);

  const saveProfile = async (e) => {
    e.preventDefault();
    setProfileMsg(null); setProfileBusy(true);
    try {
      // /auth/me aktualisiert nur den eigenen Account — funktioniert auch
      // ohne users:update Permission.
      await api.patch('/auth/me', { email, display_name: displayName });
      setProfileMsg({ type: 'success', text: 'Stammdaten aktualisiert.' });
      await refreshMe();
    } catch (err) {
      setProfileMsg({ type: 'danger', text: err.response?.data?.message || 'Speichern fehlgeschlagen' });
    } finally {
      setProfileBusy(false);
    }
  };

  const [pwCurrent, setPwCurrent] = useState('');
  const [pwNew, setPwNew] = useState('');
  const [pwConfirm, setPwConfirm] = useState('');
  const [pwError, setPwError] = useState(null);
  const [pwOk, setPwOk] = useState(false);
  const [pwBusy, setPwBusy] = useState(false);

  const [mfaState, setMfaState] = useState({ stage: 'idle' });   // idle | enrolling | done
  const [totp, setTotp] = useState('');
  const [mfaError, setMfaError] = useState(null);
  const [allowForceMfaDisable, setAllowForceMfaDisable] = useState(false);
  const [devDisableBusy, setDevDisableBusy] = useState(false);
  const [disableCode, setDisableCode] = useState('');
  const [disableBusy, setDisableBusy] = useState(false);

  useEffect(() => {
    api.get('/auth/flags').then(r => {
      setAllowForceMfaDisable(!!r.data?.allow_mfa_force_disable);
    }).catch(() => {});
  }, []);

  const pwMismatch = pwConfirm.length > 0 && pwNew !== pwConfirm;
  const pwDisabled = !pwCurrent || !pwNew || !pwConfirm || pwMismatch || pwBusy;

  const changePw = async (e) => {
    e.preventDefault();
    setPwError(null); setPwOk(false);
    if (pwNew !== pwConfirm) {
      setPwError('Die beiden neuen Passwörter stimmen nicht überein.');
      return;
    }
    setPwBusy(true);
    try {
      await api.post('/auth/change-password', { current_password: pwCurrent, new_password: pwNew });
      setPwOk(true); setPwCurrent(''); setPwNew(''); setPwConfirm('');
      await refreshMe();
    } catch (err) {
      setPwError(err.response?.data?.message || 'Passwort-Änderung fehlgeschlagen');
    } finally {
      setPwBusy(false);
    }
  };

  const beginMfa = async () => {
    setMfaError(null);
    try {
      const { data } = await api.post('/auth/mfa/begin');
      setMfaState({ stage: 'enrolling', secret: data.secret_base32, qr: data.qr_data_url });
    } catch (err) {
      setMfaError(err.response?.data?.message || 'MFA-Setup fehlgeschlagen');
    }
  };

  const completeMfa = async () => {
    setMfaError(null);
    try {
      const { data } = await api.post('/auth/mfa/complete', { totp_token: totp });
      setMfaState({ stage: 'done', recovery: data.recovery_codes });
      setTotp('');
      await refreshMe();
    } catch (err) {
      setMfaError(err.response?.data?.message || 'MFA-Verifizierung fehlgeschlagen');
    }
  };

  const disableMfa = async () => {
    setMfaError(null);
    setDisableBusy(true);
    try {
      await api.post('/auth/mfa/disable', { totp_token: disableCode });
      setDisableCode('');
      setMfaState({ stage: 'idle' });
      await refreshMe();
    } catch (err) {
      setMfaError(err.response?.data?.message || 'MFA-Deaktivierung fehlgeschlagen');
    } finally {
      setDisableBusy(false);
    }
  };

  const disableMfaDev = async () => {
    if (!confirm('MFA per Dev-Override deaktivieren? Nur in lokalen Test-Umgebungen verwenden.')) return;
    setDevDisableBusy(true);
    try {
      await api.post('/auth/mfa/disable-dev');
      setMfaState({ stage: 'idle' });
      await refreshMe();
    } catch (err) {
      setMfaError(err.response?.data?.message || 'Dev-Deaktivierung fehlgeschlagen');
    } finally {
      setDevDisableBusy(false);
    }
  };

  return (
    <>
      <PageHeader title="Profil & MFA" subtitle={user?.email} />

      <Card className="card-flat mb-3">
        <Card.Body>
          <h5 className="mb-3">Stammdaten</h5>
          {profileMsg && <Alert variant={profileMsg.type} className="py-2">{profileMsg.text}</Alert>}
          <Form onSubmit={saveProfile}>
            <Row className="g-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>E-Mail-Adresse</Form.Label>
                  <Form.Control type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
                  <Form.Text>Login-Identifier — Änderung greift sofort, beim nächsten Login mit der neuen Adresse anmelden.</Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Anzeigename</Form.Label>
                  <Form.Control type="text" value={displayName} onChange={(e) => setDisplayName(e.target.value)} required />
                </Form.Group>
              </Col>
            </Row>
            <Button type="submit" className="mt-3" disabled={profileBusy || (email === user?.email && displayName === user?.display_name)}>
              {profileBusy ? 'Speichert…' : 'Speichern'}
            </Button>
          </Form>
        </Card.Body>
      </Card>

      <Card className="card-flat mb-3">
        <Card.Body>
          <h5 className="mb-3">Passwort ändern</h5>
          {pwOk && <Alert variant="success" className="py-2">Passwort aktualisiert.</Alert>}
          {pwError && <Alert variant="danger" className="py-2">{pwError}</Alert>}
          {user?.must_change_password && (
            <Alert variant="warning" className="py-2">
              Du musst dein Initial-Passwort ändern, bevor du andere Aktionen durchführen kannst.
            </Alert>
          )}
          <Form onSubmit={changePw}>
            <Form.Group className="mb-3">
              <Form.Label>Aktuelles Passwort</Form.Label>
              <Form.Control type="password" autoComplete="current-password" required
                value={pwCurrent} onChange={(e) => setPwCurrent(e.target.value)} />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Neues Passwort</Form.Label>
              <Form.Control type="password" autoComplete="new-password" required
                value={pwNew} onChange={(e) => setPwNew(e.target.value)} />
              <Form.Text>Min. 12 Zeichen, je 1× Klein/Groß/Zahl/Sonderzeichen.</Form.Text>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Neues Passwort bestätigen</Form.Label>
              <Form.Control type="password" autoComplete="new-password" required
                value={pwConfirm} onChange={(e) => setPwConfirm(e.target.value)}
                isInvalid={pwMismatch} />
              <Form.Control.Feedback type="invalid">
                Stimmt nicht mit dem neuen Passwort überein.
              </Form.Control.Feedback>
            </Form.Group>
            <Button type="submit" disabled={pwDisabled}>
              {pwBusy ? 'Speichere…' : 'Ändern'}
            </Button>
          </Form>
        </Card.Body>
      </Card>

      <Card className="card-flat">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="mb-0">Zwei-Faktor-Authentifizierung (TOTP)</h5>
            {user?.mfa_enabled
              ? <Badge bg="" className="badge-soft-success">Aktiv</Badge>
              : <Badge bg="" className="badge-soft-warning">Nicht aktiv</Badge>}
          </div>
          {mfaError && <Alert variant="danger" className="py-2">{mfaError}</Alert>}

          {!user?.mfa_enabled && mfaState.stage === 'idle' && (
            <>
              <p className="text-muted">Aktiviere TOTP (Google Authenticator, 1Password, …) für deinen Account. Dringend empfohlen.</p>
              <Button onClick={beginMfa}><Icon icon="tabler:shield-lock" className="me-1" />MFA einrichten</Button>
            </>
          )}

          {mfaState.stage === 'enrolling' && (
            <>
              <p>1. Scanne diesen QR-Code mit deiner Authenticator-App, oder gib den Secret manuell ein:</p>
              <div className="d-flex gap-3 align-items-center mb-3">
                <img src={mfaState.qr} alt="MFA QR" style={{ width: 180, height: 180 }} />
                <code className="small bg-light p-2 rounded">{mfaState.secret}</code>
              </div>
              <p>2. Gib zur Bestätigung den 6-stelligen Code ein:</p>
              <div className="d-flex gap-2" style={{ maxWidth: 280 }}>
                <Form.Control type="text" inputMode="numeric" maxLength={6}
                  value={totp} onChange={(e) => setTotp(e.target.value.replace(/\D/g, ''))} placeholder="123456" />
                <Button onClick={completeMfa} disabled={totp.length !== 6}>Bestätigen</Button>
              </div>
            </>
          )}

          {mfaState.stage === 'done' && (
            <>
              <Alert variant="success">MFA aktiviert. Bewahre die Recovery-Codes sicher auf — jeder kann genau einmal verwendet werden.</Alert>
              <pre className="bg-light p-3 rounded">{mfaState.recovery.join('\n')}</pre>
              <Button variant="outline-secondary" onClick={() => navigator.clipboard.writeText(mfaState.recovery.join('\n'))}>Kopieren</Button>
            </>
          )}

          {user?.mfa_enabled && mfaState.stage !== 'done' && (
            <div className="d-flex gap-2 align-items-center flex-wrap">
              <Form.Control type="text" inputMode="numeric" maxLength={6} style={{ maxWidth: 140 }}
                aria-label="Aktueller MFA-Code" placeholder="Aktueller Code"
                value={disableCode} onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, ''))} />
              <Button variant="outline-danger" onClick={disableMfa} disabled={disableCode.length !== 6 || disableBusy}>
                MFA deaktivieren
              </Button>
              {allowForceMfaDisable && (
                <Button variant="outline-warning" size="sm" onClick={disableMfaDev} disabled={devDisableBusy}>
                  <Icon icon="tabler:shield-off" className="me-1" />
                  {devDisableBusy ? 'Deaktiviere…' : 'Dev-Override (kein TOTP)'}
                </Button>
              )}
            </div>
          )}
        </Card.Body>
      </Card>
    </>
  );
}
